<template>
  <div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Announcements</h5>
        <p class="page-sub">Create and manage company-wide broadcasts for employees</p>
      </div>
      <button class="btn-primary-action" @click="openCreate">
        <font-awesome-icon icon="plus" /> New Announcement
      </button>
    </div>

    <!-- Filter Bar -->
    <div class="filter-bar">
      <input
        v-model="filters.search"
        class="filter-input"
        placeholder="Search by title..."
        style="min-width:220px;"
        @input="onSearch"
      />
      <select v-model="filters.is_active" class="filter-select" @change="fetchList()">
        <option value="">All Status</option>
        <option value="1">Active</option>
        <option value="0">Inactive</option>
      </select>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div v-for="n in 5" :key="n" class="sk-row"></div>
    </div>

    <!-- Table -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Title</th>
            <th>Audience</th>
            <th>Publishes</th>
            <th>Expires</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in list" :key="item.id">
            <td>{{ (pagination.currentPage - 1) * filters.length + idx + 1 }}</td>
            <td>
              <div class="fw-600">{{ item.title }}</div>
              <div class="text-muted-sm body-preview">{{ stripHtml(item.body) }}</div>
            </td>
            <td>
              <span class="chip" :class="audienceClass(item.audience)">
                {{ audienceLabel(item.audience) }}
              </span>
            </td>
            <td>{{ fmtDate(item.publish_at) }}</td>
            <td>{{ fmtDate(item.expires_at) }}</td>
            <td>
              <span class="chip" :class="item.is_active ? 'chip--green' : 'chip--gray'">
                {{ item.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="actions-cell">
              <button class="btn-icon" title="Edit" @click="openEdit(item)">
                <font-awesome-icon icon="pen" />
              </button>
              <button class="btn-icon" :title="item.is_active ? 'Deactivate' : 'Activate'" @click="toggleStatus(item)">
                <font-awesome-icon :icon="item.is_active ? 'toggle-on' : 'toggle-off'" :class="item.is_active ? 'icon--green' : ''" />
              </button>
              <button class="btn-icon btn-icon--red" title="Delete" @click="remove(item)">
                <font-awesome-icon icon="trash" />
              </button>
            </td>
          </tr>
          <tr v-if="!list.length">
            <td colspan="7" class="empty-cell">No announcements found</td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="pagination.lastPage > 1" class="pagination">
        <button class="page-btn" :disabled="pagination.currentPage === 1" @click="goPage(pagination.currentPage - 1)">&#8249;</button>
        <span class="page-info">Page {{ pagination.currentPage }} of {{ pagination.lastPage }}</span>
        <button class="page-btn" :disabled="pagination.currentPage === pagination.lastPage" @click="goPage(pagination.currentPage + 1)">&#8250;</button>
      </div>
    </div>

    <!-- ── Create / Edit Modal ── -->
    <div v-if="modal.open" class="modal-overlay" @click.self="modal.open = false">
      <div class="modal-box">

        <div class="modal-head">
          <h5 class="modal-title">{{ modal.id ? 'Edit Announcement' : 'New Announcement' }}</h5>
          <button class="modal-close" @click="modal.open = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div class="modal-body">

          <div class="form-row">
            <label class="form-label">Title <span class="req">*</span></label>
            <input v-model="modal.title" class="form-input" placeholder="e.g. Office closure notice" maxlength="255" />
          </div>

          <div class="form-row">
            <label class="form-label">Message <span class="req">*</span></label>
            <div class="quill-wrap">
              <QuillEditor
                v-model:content="modal.body"
                content-type="html"
                :toolbar="quillToolbar"
                placeholder="Write the full announcement here..."
                theme="snow"
              />
            </div>
          </div>

          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Audience</label>
              <select v-model="modal.audience" class="form-input" @change="modal.audience_id = ''">
                <option value="all">All Employees</option>
                <option value="department">By Department</option>
                <option value="position">By Position</option>
              </select>
            </div>

            <div v-if="modal.audience === 'department'" class="form-row">
              <label class="form-label">Department</label>
              <select v-model="modal.audience_id" class="form-input">
                <option value="">— Select —</option>
                <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>

            <div v-else-if="modal.audience === 'position'" class="form-row">
              <label class="form-label">Position</label>
              <select v-model="modal.audience_id" class="form-input">
                <option value="">— Select —</option>
                <option v-for="p in positions" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>

            <div v-else class="form-row">
              <label class="form-label">&nbsp;</label>
              <div class="audience-all-note">
                <font-awesome-icon icon="users" class="me-1" /> Visible to all employees
              </div>
            </div>
          </div>

          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Publish Date <span class="form-hint">(optional)</span></label>
              <input v-model="modal.publish_at" type="date" class="form-input" />
              <span class="form-hint-text">Leave blank to publish immediately</span>
            </div>
            <div class="form-row">
              <label class="form-label">Expiry Date <span class="form-hint">(optional)</span></label>
              <input v-model="modal.expires_at" type="date" class="form-input" :min="modal.publish_at || ''" />
              <span class="form-hint-text">Leave blank for no expiry</span>
            </div>
          </div>

          <div class="form-row toggle-row">
            <span class="form-label">Active</span>
            <label class="toggle-switch">
              <input type="checkbox" v-model="modal.is_active" />
              <span class="toggle-track"></span>
            </label>
            <span class="toggle-label">{{ modal.is_active ? 'Visible to employees' : 'Hidden from employees' }}</span>
          </div>

        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="modal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving" @click="save">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving...' : (modal.id ? 'Save Changes' : 'Create Announcement') }}
          </button>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  name: 'Announcements',

  components: { QuillEditor },

  data() {
    return {
      list:    [],
      loading: false,
      saving:  false,

      quillToolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link'],
        ['clean'],
      ],

      filters: {
        search:    '',
        is_active: '',
        length:    15,
        page:      1,
      },

      pagination: { currentPage: 1, lastPage: 1 },

      departments: [],
      positions:   [],

      modal: {
        open:       false,
        id:         null,
        title:      '',
        body:       '',
        audience:   'all',
        audience_id: '',
        publish_at: '',
        expires_at: '',
        is_active:  true,
      },

      searchTimer: null,
    };
  },

  mounted() {
    this.fetchList();
    this.fetchDepartments();
    this.fetchPositions();
  },

  methods: {

    // ── Data ──────────────────────────────────────────────────────────────────
    async fetchList() {
      this.loading = true;
      try {
        const params = { ...this.filters };
        if (params.is_active === '') delete params.is_active;
        const { data } = await axios.get('/hr/announcements', { params });
        this.list = data.data?.data || data.data || [];
        if (data.data?.current_page) {
          this.pagination.currentPage = data.data.current_page;
          this.pagination.lastPage    = data.data.last_page;
        }
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load announcements');
      } finally {
        this.loading = false;
      }
    },

    async fetchDepartments() {
      try {
        const { data } = await axios.get('/payroll/department/active', { params: { status: [1], length: 200 } });
        this.departments = data.data || [];
      } catch { /* silent */ }
    },

    async fetchPositions() {
      try {
        const { data } = await axios.get('/payroll/position/active', { params: { length: 200 } });
        this.positions = data.data || [];
      } catch { /* silent */ }
    },

    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.fetchList();
      }, 400);
    },

    goPage(p) {
      this.filters.page = p;
      this.fetchList();
    },

    // ── Create / Edit ─────────────────────────────────────────────────────────
    openCreate() {
      this.modal = { open: true, id: null, title: '', body: '', audience: 'all', audience_id: '', publish_at: '', expires_at: '', is_active: true };
    },

    openEdit(item) {
      this.modal = {
        open:        true,
        id:          item.id,
        title:       item.title,
        body:        item.body,
        audience:    item.audience   || 'all',
        audience_id: item.audience_id || '',
        publish_at:  item.publish_at  ? item.publish_at.substring(0, 10) : '',
        expires_at:  item.expires_at  ? item.expires_at.substring(0, 10) : '',
        is_active:   item.is_active,
      };
    },

    stripHtml(html) {
      return (html || '').replace(/<[^>]*>/g, '').trim();
    },

    async save() {
      if (!this.modal.title.trim())          { this.$toast?.error('Title is required'); return; }
      if (!this.stripHtml(this.modal.body))  { this.$toast?.error('Message body is required'); return; }

      this.saving = true;
      const payload = {
        title:       this.modal.title,
        body:        this.modal.body,
        audience:    this.modal.audience,
        audience_id: this.modal.audience !== 'all' ? (this.modal.audience_id || null) : null,
        publish_at:  this.modal.publish_at  || null,
        expires_at:  this.modal.expires_at  || null,
        is_active:   this.modal.is_active,
      };
      try {
        if (this.modal.id) {
          await axios.put(`/hr/announcements/${this.modal.id}`, payload);
          this.$toast?.success('Announcement updated');
        } else {
          await axios.post('/hr/announcements', payload);
          this.$toast?.success('Announcement created');
        }
        this.modal.open = false;
        this.fetchList();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Save failed');
      } finally {
        this.saving = false;
      }
    },

    // ── Toggle Status ─────────────────────────────────────────────────────────
    async toggleStatus(item) {
      try {
        await axios.put(`/hr/announcements/${item.id}/status`);
        item.is_active = !item.is_active;
        this.$toast?.success('Status updated');
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Update failed');
      }
    },

    // ── Delete ────────────────────────────────────────────────────────────────
    async remove(item) {
      if (!confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
      try {
        await axios.delete(`/hr/announcements/${item.id}`);
        this.$toast?.success('Announcement deleted');
        this.fetchList();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Delete failed');
      }
    },

    // ── Helpers ───────────────────────────────────────────────────────────────
    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    audienceLabel(a) {
      return { all: 'All', department: 'Department', position: 'Position' }[a] || a;
    },

    audienceClass(a) {
      return { all: 'chip--blue', department: 'chip--purple', position: 'chip--amber' }[a] || 'chip--gray';
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }

.filter-bar   { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.filter-input,.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; font-family:inherit; }
.filter-input:focus,.filter-select:focus { border-color:#004AAD; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }

.table-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }
.empty-cell  { text-align:center; color:#aab8cc; padding:40px !important; }
.actions-cell { white-space:nowrap; }
.fw-600 { font-weight:600; }
.text-muted-sm { font-size:11px; color:#7a90b0; margin-top:2px; }
.body-preview { max-width:280px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

.btn-icon     { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:14px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red:hover { color:#dc2626; }
.icon--green  { color:#16a34a; }

.chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--gray   { background:#f1f5f9; color:#64748b; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--purple { background:#ede9fe; color:#7c3aed; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }

.pagination { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #f0f4f8; }
.page-btn   { background:#fff; border:1px solid #dde5f0; border-radius:6px; padding:4px 12px; font-size:16px; cursor:pointer; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info  { font-size:13px; color:#7a90b0; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Modal */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:640px; max-height:92vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; position:sticky; top:0; background:#fff; z-index:1; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.form-cols     { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-row      { display:flex; flex-direction:column; gap:5px; }
.form-label    { font-size:12px; font-weight:600; color:#4a5e78; }
.form-hint     { font-size:11px; font-weight:400; color:#7a90b0; }
.form-hint-text { font-size:11px; color:#aab8cc; }
.form-input    { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.form-textarea { resize:vertical; min-height:100px; line-height:1.5; }
.req { color:#dc2626; }

/* Quill editor */
.quill-wrap { border:1px solid #dde5f0; border-radius:8px; overflow:hidden; display:flex; flex-direction:column; }
.quill-wrap:focus-within { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.quill-wrap :deep(.ql-toolbar) { border:none; border-bottom:1px solid #e4eaf2; background:#f8fafc; padding:6px 8px; flex-shrink:0; }
.quill-wrap :deep(.ql-container) { border:none; font-size:13.5px; font-family:inherit; height:auto !important; min-height:150px; flex:1; }
.quill-wrap :deep(.ql-editor) { height:auto !important; min-height:150px; padding:12px 14px; color:#1a2b45; line-height:1.6; }
.quill-wrap :deep(.ql-editor.ql-blank::before) { color:#aab8cc; font-style:normal; }
.quill-wrap :deep(.ql-toolbar button:hover .ql-stroke),
.quill-wrap :deep(.ql-toolbar button.ql-active .ql-stroke) { stroke:#004AAD; }
.quill-wrap :deep(.ql-toolbar button:hover .ql-fill),
.quill-wrap :deep(.ql-toolbar button.ql-active .ql-fill) { fill:#004AAD; }
.quill-wrap :deep(.ql-toolbar .ql-picker-label:hover),
.quill-wrap :deep(.ql-toolbar .ql-picker-label.ql-active) { color:#004AAD; }

.audience-all-note {
  display: flex;
  align-items: center;
  padding: 9px 12px;
  background: #f0f7ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 12.5px;
  color: #1e40af;
  font-weight: 500;
  margin-top: 17px;
}
.me-1 { margin-right:4px; }

.toggle-row   { flex-direction:row; align-items:center; gap:10px; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }
.toggle-label { font-size:12px; color:#7a90b0; }

.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#e4eaf2; }

@media (max-width:600px) {
  .form-cols { grid-template-columns:1fr; }
}
</style>
